import React, { useContext, useEffect, useRef, useState } from 'react'
import type { Map as MapLibreMap, Marker } from 'maplibre-gl'
import 'maplibre-gl/dist/maplibre-gl.css'
import LocationContext from './LocationContext'
import { GlobalContext } from '../../GlobalContext'
const mapTilerAPIKey = '1j1RqlmNhygo7wmzR2gu'

type Props = {
    children?: React.ReactNode
}

export default function LocationProvider({ children }: Props) {
    const mapContainerRef = useRef<HTMLDivElement | null>(null)
    const mapRef = useRef<MapLibreMap | null>(null)
    const emojiMarkerRef = useRef<Marker | null>(null)
    const gpsMarkerRef = useRef<Marker | null>(null)
    const [locationFromGps, setLocationFromGps] = useState<Coordinates | null>(null)
    const [locationFromUser, setLocationFromUser] = useState<Coordinates | null>(null)
    const [locationAsString, setLocationAsString] = useState<string>('')
    const [locationError, setLocationError] = useState<string | null>(null)
    const [personEmoji, setPersonEmoji] = useState('🧍‍♀️')
    const { isMapMode } = useContext(GlobalContext)

    useEffect(() => {
        if (!isMapMode || mapRef.current || !mapContainerRef.current) return

        let cancelled = false
        const container = mapContainerRef.current
        const defaultCenter: [number, number] = locationFromGps
            ? [locationFromGps.lng, locationFromGps.lat]
            : [-0.1278, 51.5074]

        import(/* webpackChunkName: "maplibre-gl" */ 'maplibre-gl').then((ml) => {
            if (cancelled || !container) return
            const maplibregl = ml.default

            const map = new maplibregl.Map({
                container,
                style: `https://api.maptiler.com/maps/streets-v2/style.json?key=${mapTilerAPIKey}`,
                center: defaultCenter,
                zoom: 13,
            })
            mapRef.current = map

            const emojiEl = document.createElement('div')
            emojiEl.style.fontSize = '30px'
            emojiEl.style.width = '40px'
            emojiEl.style.height = '40px'
            emojiEl.style.textAlign = 'center'
            emojiEl.style.lineHeight = '40px'
            emojiEl.style.borderRadius = '50%'
            emojiEl.style.background = 'rgba(255,255,255,0.85)'
            emojiEl.style.boxShadow = '0 0 4px rgba(0,0,0,0.4)'
            emojiEl.innerText = personEmoji
            emojiMarkerRef.current = new maplibregl.Marker({ element: emojiEl })
                .setLngLat([map.getCenter().lng, map.getCenter().lat])
                .addTo(map)

            const gpsEl = document.createElement('div')
            gpsEl.style.width = '14px'
            gpsEl.style.height = '14px'
            gpsEl.style.borderRadius = '50%'
            gpsEl.style.background = '#0d6efd'
            gpsEl.style.boxShadow = '0 0 6px rgba(0,0,0,0.5)'
            gpsMarkerRef.current = new maplibregl.Marker({ element: gpsEl })
                .setLngLat(defaultCenter)
                .addTo(map)

            map.on('move', () => {
                const c = map.getCenter()
                setLocationFromUser({ lat: c.lat, lng: c.lng })
                setPersonEmoji('🚶‍♂️')
                emojiMarkerRef.current?.setLngLat([c.lng, c.lat])
            })
            map.on('moveend', () => {
                const c = map.getCenter()
                setLocationFromUser({ lat: c.lat, lng: c.lng })
                setPersonEmoji('🧍‍♀️')
                emojiMarkerRef.current?.setLngLat([c.lng, c.lat])
            })
            map.on('error', (e: any) => { if (!e.tile) console.error('map error', e) })
        })

        return () => {
            cancelled = true
            mapRef.current?.remove()
            mapRef.current = null
            emojiMarkerRef.current = null
            gpsMarkerRef.current = null
        }
    }, [isMapMode])

    useEffect(() => {
        if (!locationFromGps) return
        const oldLat = gpsMarkerRef.current?.getLngLat().lat || 0
        const oldLng = gpsMarkerRef.current?.getLngLat().lng || 0
        const bigChange = Math.abs(oldLat - locationFromGps.lat) > 1 || Math.abs(oldLng - locationFromGps.lng) > 1
        gpsMarkerRef.current?.setLngLat([locationFromGps.lng, locationFromGps.lat])
        if (bigChange) mapRef.current?.easeTo({ center: [locationFromGps.lng, locationFromGps.lat], duration: 300 })
    }, [locationFromGps])

    useEffect(() => {
        const el = emojiMarkerRef.current?.getElement()
        if (el) el.textContent = personEmoji
    }, [personEmoji])

    useEffect(() => {
        if (!navigator.geolocation) return
        const watchId = navigator.geolocation.watchPosition(
            (pos) => setLocationFromGps({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
            (err) => setLocationError(err.message || 'Geolocation error')
        )
        return () => navigator.geolocation.clearWatch(watchId)
    }, [])

    const contextValue = {
        locationFromGps,
        locationFromUser,
        locationAsString,
        locationError,
        setLocationFromGps,
        setLocationFromUser,
        setLocationAsString,
        setLocationError
    }

    if (!isMapMode) {
        return <LocationContext.Provider value={contextValue}>
            {children}
        </LocationContext.Provider>
    }

    return <LocationContext.Provider value={contextValue}>
        <div className="fixed z-10 w-full">{children}</div>
        <div ref={mapContainerRef} className="absolute top-0 left-0 h-screen w-screen" />
    </LocationContext.Provider>
}
